import Vue from "vue";
import {
    EmailHistoryState,
    EmailHistoryEvent,
    EmailHistoryRawEvent,
    EmailHistoryEventSource
} from "../types";
import * as UserService from "@/shared/api/userService";
import { FetchState } from "@/shared/types";
import * as storeHelper from "@/shared/storeHelper";
import { defineStore } from "pinia";
import _ from "lodash";
import { useNotifierStore } from "@/shared/store/notifier";

export const initialState: EmailHistoryState = {
    fetchState: FetchState.Uninitialized,
    history: [],
    email: null,
    rawEventDictionary: []
};

export const useEmailHistoryStore = defineStore("emailHistory", {
    state: (): EmailHistoryState => (_.cloneDeep(initialState)),
    getters: {
        isLoading: state => state.fetchState === FetchState.Loading,
        isLoaded: state => state.fetchState === FetchState.Loaded,
        isRawDataLoaded: state => id => state.rawEventDictionary[id] && state.rawEventDictionary[id].fetchState ===
            FetchState.Loaded,
        rawData: state => id => state.rawEventDictionary[id].data,
        oktaRawData: state => (messageId: string) => {
            const record = state.history.find(
                e => e.source === EmailHistoryEventSource.Okta && e.sesMessageId === messageId);
            return record ? record.rawEvent : "Unable to load details.";
        }
    },
    actions: {
        setFetchState (fetchState: FetchState): void {
            this.fetchState = fetchState;
        },
        setHistory (history: EmailHistoryEvent[]) {
            this.history = history;
        },
        clearHistory () {
            this.history = [];
            this.email = null;
        },
        setRawData (params: { id: number, data: string }) {
            const record = this.rawEventDictionary[params.id];
            record.data = params.data;
        },
        setRawDataFetchState (id: number) {
            const record = this.rawEventDictionary[id];
            record.fetchState = FetchState.Loaded;
        },
        setRawDataLoading (id: number) {
            const record = this.rawEventDictionary[id];
            if (!record) {
                const entry: EmailHistoryRawEvent = {
                    fetchState: FetchState.Loading,
                    data: null
                };
                Vue.set(this.rawEventDictionary, id, entry);
            }
        },
        loadHistory (email: string) {
            this.clearHistory();
            this.setFetchState({ fetchState: FetchState.Loading });

            return UserService.getEmailHistory(email).then(response => {
                this.setHistory(response.data);
                this.setFetchState(FetchState.Loaded);
            }).catch(err => {
                this.clearHistory();
                this.setFetchState(FetchState.Uninitialized);
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        loadRawEvent (historyId: number) {
            const rawEventRecord = this.rawEventDictionary[historyId];
            if (rawEventRecord && rawEventRecord.fetchState !== FetchState.Uninitialized) {
                return;
            }
            this.setRawDataLoading(historyId);

            return UserService.getEmailHistoryRawEventData(historyId).then(response => {
                this.setRawData({ id: historyId, data: response.data });
                this.setRawDataFetchState(historyId);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        removeFromBounceList (emailAddress: string) {
            return UserService.removeEmailFromBounceList(emailAddress)
                .then(() => {
                    const notifierStore = useNotifierStore();
                    notifierStore.setSuccess("The email will be removed within 2-5 minutes.");
                })
                .catch(error => {
                    storeHelper.handleError({ error, rethrow: false });
                });
        }
    }
});